package gr.tech.pdfgenerator.sections

import gr.tech.pdfgenerator.util.formatCurrency
import gr.tech.pdfgenerator.util.roundCents
import gr.tech.pdfgenerator.util.roundToOneDecimalPlace
import gr.tech.pdfgenerator.util.toDollars
import gr.tech.pdfgenerator.styles.Theme
import kotlinx.browser.document
import org.jetbrains.letsPlot.Stat
import org.jetbrains.letsPlot.annotations.layerLabels
import org.jetbrains.letsPlot.frontend.JsFrontendUtil
import org.jetbrains.letsPlot.geom.geomBar
import org.jetbrains.letsPlot.geom.geomHLine
import org.jetbrains.letsPlot.geom.geomPie
import org.jetbrains.letsPlot.ggplot
import org.jetbrains.letsPlot.ggsize
import org.jetbrains.letsPlot.label.ggtitle
import org.jetbrains.letsPlot.label.labs
import org.jetbrains.letsPlot.label.xlab
import org.jetbrains.letsPlot.label.ylab
import org.jetbrains.letsPlot.letsPlot
import org.jetbrains.letsPlot.pos.positionDodge
import org.jetbrains.letsPlot.scale.scaleFillGradient
import org.jetbrains.letsPlot.scale.scaleFillManual
import org.jetbrains.letsPlot.scale.scaleXDiscrete
import org.jetbrains.letsPlot.themes.elementBlank
import org.jetbrains.letsPlot.themes.elementText
import org.jetbrains.letsPlot.themes.theme


fun updateCashflowGraph(cashList: MutableList<Long>) {
//    console.log("GRAPH: Updating CashFlow graph..")
    val contentDiv = document.getElementById("cashflow-graph")
    if (contentDiv != null && contentDiv.hasChildNodes()) {
        val graph = contentDiv.firstChild
        if (graph != null) {
            contentDiv.removeChild(graph)
        }
    }

    val formattedCashList = cashList.map { it.toDollars() }
    val years = cashList.indices.map { it + 1}
    val data = mapOf<String, Any> (
        "x" to years,
        "y" to formattedCashList
    )

    val plot = letsPlot(data) +
            geomBar(
                stat = Stat.identity,
                color = "dark-green",
                fill = "green",
                alpha = .3,
                size = 2.0,
            ) { x = "x"; y = "y" } +
            ylab("Energy Savings ($)") +
            ggtitle("Energy Saving")

    val plotDiv = JsFrontendUtil.createPlotDiv(plot)
    contentDiv?.appendChild(plotDiv)
}

fun updateCummulCashFlowGraph(cashList: MutableList<Long>) {
//    console.log("GRAPH: Updating Cummulative Flow graph..")
    val contentDiv = document.getElementById("cummulflow-graph")
    if (contentDiv != null && contentDiv.hasChildNodes()) {
        val graph = contentDiv.firstChild
        if (graph != null) {
            contentDiv.removeChild(graph)
        }
    }

    val formattedCashList = cashList.map { it.toDollars() }
    val years = cashList.indices.map { it + 1}
    val data = mapOf<String, Any> (
        "x" to years,
        "y" to formattedCashList
    )

    val plot = letsPlot(data) +
            geomBar(
                stat = Stat.identity,
                color = "dark-green",
                fill = "green",
                alpha = .3,
                size = 2.0,
            ) { x = "x"; y = "y" } +
            ylab("Energy Savings ($)") +
            ggtitle("Cumulative Cash Flow")

    val plotDiv = JsFrontendUtil.createPlotDiv(plot)
    contentDiv?.appendChild(plotDiv)
}

fun updatePDFCummulCashFlowGraph(cashList: MutableList<Long>) {
    val contentDiv = document.getElementById("pdfcummulflow-graph")
    if (contentDiv != null) {
        // Remove existing graph if present
        val existingGraph = contentDiv.querySelector(".cummul-flow-chart")
        if (existingGraph != null) {
            contentDiv.removeChild(existingGraph)
        }

        // Check if there's data to plot
        if (cashList.isNotEmpty()) {
            val years = cashList.indices.map { "Year ${it + 1}" }
            val data = mapOf(
                "x" to years,
                "y" to cashList.map { it.toDollars() },
                "BL" to cashList.map { formatCurrency(it.roundCents()) }
            )

            val plot = letsPlot(data) +
                    geomBar(
                        stat = Stat.identity,
                        position = positionDodge(width = 0.8),  // Add space between bars
                        width = 0.8,
                        color = "#dee4e4",
                        fill = "#dee4e4",
                        alpha = 1.0,
                        size = 1.0,
                        labels = layerLabels().line("@BL").size(12),
                    ) { x = "x"; y = "y" } +
                    geomHLine (
                        yintercept = (0),
                        color = "#dee4e4",
                        size = 0.5,
                        linetype = "solid"
                    ) +
                    ggsize(700, 450) +
                    xlab("") +
                    ylab("") +
                    theme(
                        axisTextY = elementBlank(),
                        axisTicksY = elementBlank(),
                        axisLineY = elementBlank(),
                        axisTextX = elementText(size = 10, color = "Grey"),
                        axisTicksX = elementBlank(),
                        axisLineX = elementBlank(),
                        panelGrid = elementBlank(),
                        panelBackground = elementBlank(),
                        plotBackground = elementBlank(),
                        plotTitle = elementText(hjust = 0.5),
                    )


            val plotDiv = JsFrontendUtil.createPlotDiv(plot)
            plotDiv.classList.add("cummul-flow-chart")
            contentDiv.appendChild(plotDiv)
        }
    }
}

fun updateCostWaitingGraph(cashList: MutableList<Long>) {
//    console.log("GRAPH: Updating Cost Waiting Flow graph..")
    val contentDiv = document.getElementById("costWaiting-graph")
    if (contentDiv != null && contentDiv.hasChildNodes()) {
        val graph = contentDiv.firstChild
        if (graph != null) {
            contentDiv.removeChild(graph)
        }
    }

    val formattedCashList = cashList.map { it.toDollars() }
    val years = cashList.indices.map { it + 1}
    val data = mapOf<String, Any> (
        "x" to years,
        "y" to formattedCashList
    )

    val plot = letsPlot(data) +
            geomBar(
                stat = Stat.identity,
                color = "dark-green",
                fill = "green",
                alpha = .3,
                size = 2.0,
            ) { x = "x"; y = "y" } +
            xlab("Years") +
            ylab("Energy Savings ($)")

    val plotDiv = JsFrontendUtil.createPlotDiv(plot)
    contentDiv?.appendChild(plotDiv)
}

fun updatePDFCostWaitingGraph(cashList: MutableList<Long>) {
    val contentDiv = document.getElementById("pdfcostWaiting-graph")
    if (contentDiv != null && contentDiv.hasChildNodes()) {
        val graph = contentDiv.firstChild
        if (graph != null) {
            contentDiv.removeChild(graph)
        }
    }

        // Check if there's data to plot
    val years = cashList.indices.map { "${it + 1} yrs" }
    val data = mapOf(
        "x" to years,
        "y" to cashList.map { it.toDollars() },
        "BL" to cashList.map { formatCurrency(it.roundCents()) }
    )

    val plot = letsPlot(data) +
            geomBar(
                stat = Stat.identity,
                position = positionDodge(width = 1),  // Add space between bars
                width = 0.7,
                color = "#dee4e4",
                fill = "#dee4e4",
                alpha = 1.0,
                size = 1,
                labels = layerLabels().line("@BL").size(18),
            ) { x = "x"; y = "y" } +
            geomHLine (
                yintercept = (0),
                color = "#dee4e4",
                size = 0.5,
                linetype = "solid"
            ) +
            ggsize(650, 450) +
            xlab("") +
            ylab("") +
            theme(
                axisTextY = elementBlank(),
                axisTicksY = elementBlank(),
                axisLineY = elementBlank(),
                axisTextX = elementText(size = 12, color = "Grey"),
                axisTicksX = elementBlank(),
                axisLineX = elementBlank(),
                panelGrid = elementBlank(),
                panelBackground = elementBlank(),
                plotBackground = elementBlank(),
                plotTitle = elementText(hjust = 0.5),
            )


    val plotDiv = JsFrontendUtil.createPlotDiv(plot)
    contentDiv?.appendChild(plotDiv)
}

fun updateSavingsAnalysisGraph(ledPercentage: Double, baselinePercentage: Double) {
//    console.log("GRAPH: Updating Savings Analysis graph..")
    val contentDiv = document.getElementById("savingsAnalysis-graph")
    if (contentDiv != null && contentDiv.hasChildNodes()) {
        val graph = contentDiv.firstChild
        if (graph != null) {
            contentDiv.removeChild(graph)
        }
    }

    val data = mapOf<String, Any>(
        "type" to listOf("LED", "Baseline"),
        "value" to listOf(ledPercentage, baselinePercentage),
        "labels" to listOf("${ledPercentage.roundToOneDecimalPlace()}", "${baselinePercentage.roundToOneDecimalPlace()}")
    )

    // Create the pie chart
    val blankTheme = theme(line=elementBlank(), axis=elementBlank())
    val plot = letsPlot(data) +
            geomPie(
                stat = Stat.identity,
                size = 25,
                stroke = 1,
                labels = layerLabels().line("@labels").size(16)
            ) { slice = "value"; fill = "type" } +
            ggsize(500, 500) +
            scaleFillGradient(low = "orange", high = "dark_green") +
            blankTheme

    // Add the plot to the contentDiv
    val plotDiv = JsFrontendUtil.createPlotDiv(plot)
    contentDiv?.appendChild(plotDiv)
}

fun updateSavingsBarGraph(
    baselineEnergyCosts: Long,
    ledEnergyCosts: Long,
    baselineMaintenance: Long,
    baselineHVACCost: Long,
    ledHVACCost: Long,
    baselineTotalCost: Long,
    ledTotalCost: Long,
) {
//    console.log("GRAPH: Updating Savings Bar graph..")
    val contentDiv = document.getElementById("savingsBar-graph")
    if (contentDiv != null && contentDiv.hasChildNodes()) {
        val graph = contentDiv.firstChild
        if (graph != null) {
            contentDiv.removeChild(graph)
        }
    }

    val baselineEC = baselineEnergyCosts.toDollars()
    val ledEC = ledEnergyCosts.toDollars()
    val baselineMaint = baselineMaintenance.toDollars()
    val ledMaint = 0L
    val baselineHVAC = baselineHVACCost.toDollars()
    val ledHVAC = ledHVACCost.toDollars()
    val baselineTotal = baselineTotalCost.toDollars()
    val ledTotal = ledTotalCost.toDollars()

    // Prepare data with grouping
    val data = mapOf(
        "Category" to listOf("Energy Costs", "Energy Costs", "Maintenance", "Maintenance", "HVAC", "HVAC", "Total", "Total"),
        "Type" to listOf("Baseline", "LED", "Baseline", "LED", "Baseline", "LED", "Baseline", "LED"),
        "Value" to listOf(baselineEC, ledEC, baselineMaint, ledMaint, baselineHVAC, ledHVAC, baselineTotal, ledTotal)
    )

    val plot = ggplot(data) { x = "Category"; fill = "Type" } +
            geomBar(
                stat = Stat.identity,
                position = positionDodge(1),
                color = "dark-green",
                alpha = 0.7
            ) { y = "Value" } +
            ggsize(700, 400) +
            scaleXDiscrete(labels = listOf("Energy Costs", "Maintenance", "HVAC", "Total")) +
            scaleFillManual(values = mapOf("Baseline" to "blue", "LED" to "dark-green")) +
            labs(y = "Total in $")

    val plotDiv = JsFrontendUtil.createPlotDiv(plot)
    contentDiv?.appendChild(plotDiv)
}

fun updatePDFSavingsBarGraph(
    baselineEnergyCosts: Long,
    ledEnergyCosts: Long,
    baselineMaintenance: Long,
    baselineHVACCost: Long,
    ledHVACCost: Long,
    baselineTotalCost: Long,
    ledTotalCost: Long,
) {
//    console.log("GRAPH: Updating Savings Bar graph..")
    val contentDiv = document.getElementById("pdfSavingsBar-graph")
    if (contentDiv != null && contentDiv.hasChildNodes()) {
        val graph = contentDiv.firstChild
        if (graph != null) {
            contentDiv.removeChild(graph)
        }
    }

    val baselineEC = baselineEnergyCosts
    val ledEC = ledEnergyCosts
    val baselineMaint = baselineMaintenance
    val ledMaint = 0L
    val baselineHVAC = baselineHVACCost
    val ledHVAC = ledHVACCost
    val baselineTotal = baselineTotalCost
    val ledTotal = ledTotalCost
    val valueList = listOf(baselineEC, ledEC, baselineMaint, ledMaint, baselineHVAC, ledHVAC, baselineTotal, ledTotal)

    // Prepare data with grouping
    val data = mapOf(
        "Category" to listOf("Energy Costs", "Energy Costs", "Maintenance", "Maintenance", "HVAC", "HVAC", "Total", "Total"),
        "Type" to listOf("Baseline", "LED", "Baseline", "LED", "Baseline", "LED", "Baseline", "LED"),
        "Value" to valueList.map { it.toDollars() },
        "BL" to valueList.map { formatCurrency(it.roundCents()) }
    )

    val plot = ggplot(data) { x = "Category"; fill = "Type" } +
            geomBar(
                stat = Stat.identity,
                position = positionDodge(0.9),
                width = 0.7,
                alpha = 1,
                size = 0.8,
                labels = layerLabels().line("@BL").size(14),
                showLegend = false
            ) { y = "Value" } +
            ggsize(700, 375) +
            scaleXDiscrete(labels = listOf("Energy Costs", "Maintenance", "HVAC", "Total")) +
            scaleFillManual(values = mapOf("Baseline" to Theme.MediumGreen.rgb.toString(), "LED" to Theme.DarkGreen.rgb.toString())) +
            geomHLine (
                yintercept = (0),
                color = "#dee4e4",
                size = 0.5,
                linetype = "solid"
            ) +
            xlab("") +
            ylab("") +
            theme(
                axisTextY = elementBlank(),
                axisTicksY = elementBlank(),
                axisLineY = elementBlank(),
                axisTextX = elementText(size = 12, color = "Grey"),
                axisTicksX = elementBlank(),
                axisLineX = elementBlank(),
                panelGrid = elementBlank(),
                panelBackground = elementBlank(),
                plotBackground = elementBlank(),
                plotTitle = elementText(hjust = 0.5),
            )

    val plotDiv = JsFrontendUtil.createPlotDiv(plot)
    contentDiv?.appendChild(plotDiv)
}

fun updateFinanceUpFrontBarGraph() {

}

fun updateAnnualReturnBarGraph() {

}